import { styled } from "goober";
import { navEnterStyles } from "../hooks/useNavTransition";
import { usePanelTransition } from "./PanelTransition";
import { queries } from "../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useRunExecutions } from "../hooks/useStepExecutions";
import type { Connector } from "../lib/connector";
import type { ParsedStep } from "../lib/parseCommitData";
import { WorkflowConnectorsPanel } from "./WorkflowConnectorsPanel";
import { WorkflowGraphPanel } from "./WorkflowGraphPanel";
import { WorkflowStepsPanel } from "./WorkflowStepsPanel";
import { WorkflowStepTestsPanel } from "./WorkflowStepTestsPanel";

type Props = {
  workflowId: string;
  branchId: string;
  canWrite: boolean;
  steps: ReadonlyArray<ParsedStep>;
  connectors: ReadonlyArray<Connector>;
  onStepsHover?: (canonicalIds: ReadonlyArray<string> | null) => void;
  onAddTests?: () => void;
  onInvestigateFailingSteps?: (stepNames: readonly string[]) => void;
};

const BAR_HEIGHT = 160;

const Outer = styled("div")`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  container-type: inline-size;
`;

const Card = styled("div")`
  display: flex;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const CardContent = styled("div")`
  ${navEnterStyles}
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
`;

const GraphSlot = styled(Card)`
  flex: 1;
  min-height: 200px;
`;

const BarsRow = styled("div")`
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  gap: 12px;
  height: ${BAR_HEIGHT}px;

  @container (max-width: 600px) {
    flex-direction: column;
    height: auto;
  }
`;

const BarCard = styled(Card)`
  flex: 1;
  min-width: 0;
`;

export function WorkflowMonitorPanel({
  workflowId,
  branchId,
  canWrite,
  steps,
  connectors,
  onStepsHover,
  onAddTests,
  onInvestigateFailingSteps,
}: Props) {
  const direction = usePanelTransition();
  const [runs, runsQuery] = useQuery(
    queries.loadBranchRunsSummary({ branchId })
  );
  const latestRunId = runs.find((r) => !r.ephemeral)?.id ?? null;
  const [latestRunRow] = useQuery(
    latestRunId ? queries.loadRunSummary({ runId: latestRunId }) : undefined
  );
  const { executions: latestRunExecutions } = useRunExecutions(latestRunId);
  const latestRun = latestRunRow
    ? { ...latestRunRow, executions: latestRunExecutions }
    : null;
  const showGhostGraph = runsQuery.type === "complete" && runs.length === 0;
  return (
    <Outer>
      <GraphSlot>
        <CardContent data-nav-enter={direction}>
          <WorkflowGraphPanel
            runs={runs}
            latestRun={latestRun}
            ghost={showGhostGraph}
            ghostSeed={workflowId}
          />
        </CardContent>
      </GraphSlot>
      <BarsRow>
        <BarCard>
          <CardContent data-nav-enter={direction}>
            <WorkflowStepsPanel
              canWrite={canWrite}
              steps={steps}
              onStepsHover={onStepsHover}
              onInvestigateFailingSteps={onInvestigateFailingSteps}
            />
          </CardContent>
        </BarCard>
        <BarCard>
          <CardContent data-nav-enter={direction}>
            <WorkflowConnectorsPanel
              connectors={connectors}
              steps={steps}
              onStepsHover={onStepsHover}
            />
          </CardContent>
        </BarCard>
        <BarCard>
          <CardContent data-nav-enter={direction}>
            <WorkflowStepTestsPanel
              canWrite={canWrite}
              workflowId={workflowId}
              branchId={branchId}
              onStepsHover={onStepsHover}
              onAddTests={onAddTests}
            />
          </CardContent>
        </BarCard>
      </BarsRow>
    </Outer>
  );
}
